import './rewards.css';

import EventEmitter from "eventemitter3";
import { EventTypes } from '../../../core/event-bus';
import { Data } from "../../../models/data";
import { Order } from '../../../models/order';
import { ProfileService } from "../../../services/profile-service";
import { DataLayerService } from '../../../services/data-layer-service';
import { LocalizationService } from '../../../services/localization-service';
import { LoaderSpinner } from "../../shared/LoaderSpinner/loader-spinner";
import { IUserLoyalty, IUserRewardOffersDTO, IUserRewardOffersDetails } from '../../../models/user.interface';
import { IDataRedeemItemDisplayGroup, IDataReward } from '../../../models/data.interface';
import { IMyHistory, IMyHistoryDetails, IRewardsPaginationState } from './rewards.interface';
import { IName } from "../../../interfaces/global.interfaces";
import { Util } from '../../../utils/util';
import { Page } from "../page";
import { Modals, Pages, LoyaltyProgramType, ContentType } from '../../../types/enums';
import { Router } from '../../../core/router';
import { Context } from '../../../models/context';
import { IRewardGroupItemsProps } from './../../modals/RewardGroupItems/reward-group-items';
import { ILocationProps } from '../Locations/locations.interface';

export class Rewards extends Page {
    private readonly _profileService: ProfileService;
    private readonly _dataLayer: DataLayerService;
    private readonly _order: Order;
    private readonly _maxRewardsHistoryDays = 30;
    private readonly _maxPerPage = 5;
    private readonly _dateFormatOptions: Intl.DateTimeFormatOptions = {
        year: "numeric",
        month: "short",
        day: "numeric",
    };
    private _localCulture: string;
    private _historyDate: string;
    private _loyaltyData: IUserLoyalty | null;
    private _nextRewardInfo: { points: number, reward: IDataReward | null } | null;
    private _offersData: IUserRewardOffersDetails[] | null;
    private _rewardsHistory: IMyHistoryDetails[] | null;
    private _pendingRewardRedemption: { rewardId?: string, couponCode?: string } | null;
    private _paginationState: IRewardsPaginationState;

    constructor(
        eventBus: EventEmitter,
        data: Data,
        context: Context,
        localizationService: LocalizationService,
        profileService: ProfileService,
        dataLayer: DataLayerService,
        order: Order,
    ) {
        super("page_rewards", eventBus, data, context, localizationService);
        this._profileService = profileService;
        this._dataLayer = dataLayer;
        this._order = order;
        this._localCulture = "en-us";
        this._historyDate = new Date(new Date().getTime() - (this._maxRewardsHistoryDays * 24 * 60 * 60 * 1000)).toLocaleDateString();
        this._loyaltyData = null;
        this._nextRewardInfo = null;
        this._offersData = null;
        this._rewardsHistory = null;
        this._pendingRewardRedemption = null;
        this._paginationState = { offersPagesOpened: 1, historyPagesOpened: 1 };
        this._eventBusListeners();
    }

    public async initAsync(): Promise<void> {
    }

    /**
     * Shows the rewards page if the user is logged in.
     */
    public override show(): void {
        super.show();

        if (!this._profileService.user.isLoggedIn()) {
            this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.LANDING)
            this._eventBus.emit(EventTypes.MODAL_OPEN, Modals.LOGIN)
        }

        this._buildRewardsSection();

        this._context.setProperty("upsellSelected", false);
        if (this._dataLayer)
            this._dataLayer.page_view(this._profileService.user.isLoggedIn(), this._data.storeKey ? this._data.getProperty("StoreInfo").storeCode.toString() : '0', this._profileService.user.user);
    }

    /**
     * Sets the event bus listeners for the rewards page.
     */
    private readonly _eventBusListeners = () => {
        this._eventBus.on(EventTypes.LOGGED_OUT, this._redirectToHome)
        this._eventBus.on(EventTypes.REWARD_REMOVED, this._updateOfferCard);
        this._eventBus.on(EventTypes.LANGUAGE_CHANGED, this._buildRewardsSection);
        this._eventBus.on(EventTypes.LOCATION_CHANGE, (location: Pages, rewardId?: unknown, couponCode?: string) => {
            // Back/forward passes the router's { back: true } options here, which isn't a reward id.
            this._onContinueRedeem(location, typeof rewardId === "string" ? rewardId : undefined, couponCode);
        });
        this._eventBus.on(EventTypes.STORE_CHANGED, this._redeemPendingReward);
    }

    private readonly _onContinueRedeem = (page: Pages, rewardId?: string, couponCode?: string): void => {
        if (page !== Pages.REWARDS) {
            return;
        }

        if (rewardId || couponCode)
            this._pendingRewardRedemption = { rewardId: rewardId, couponCode: couponCode };
    }

    private readonly _redeemPendingReward = async (): Promise<void> => {
        if (this._pendingRewardRedemption) {
            await this._redeemOnClick(this._pendingRewardRedemption.rewardId, this._pendingRewardRedemption.couponCode);
            this._pendingRewardRedemption = null;
        }
    }

    /**
     * Builds the rewards section of the page.
     */
    private readonly _buildRewardsSection = async (): Promise<void> => {
        if (Router.path != Pages.REWARDS) return;

        this._clearData();
        await this._populateData();
        this._clearRewardsSection()
        await this._renderPageAsync();
        this._setEventListeners();
    }

    /**
     * Redirects the user after logout (see Page._redirectAfterLogout).
     */
    private readonly _redirectToHome = (): void => {
        this._clearRewardsSection();
        this._clearData();
        //skip redirect to home if there was a target store key on initial page load
        if (this._context.getProperty("targetStoreKey")) {
            return;
        }

        if (Router.path === Pages.REWARDS) {
            this._redirectAfterLogout();
        }
    }

    /**
     * Clears the rewards section of the page and resets the offers and rewwards history paginated display status back to false.
     */
    private readonly _clearRewardsSection = (): void => {
        this._offersData?.forEach(x => x.isDisplayed = false);
        this._rewardsHistory?.forEach(x => x.isDisplayed = false);
        Util.setElement("innerHTML", "div_points_summary_main", "");
        Util.setElement("innerHTML", "div_user_info_main", "");
        Util.setElement("innerHTML", "div_redeem_points_main", "");
        Util.setElement("innerHTML", "div_offers_main", "");
        Util.setElement("innerHTML", "div_rewards_activity_main", "");
    }

    /**
     * Clears the rewards page data.
     */
    private readonly _clearData = (): void => {
        this._loyaltyData = null;
        this._offersData = null;
        this._nextRewardInfo = null;
        this._rewardsHistory = null;
    }

    /**
     * Populates the rewards page data.
     */
    private readonly _populateData = async (): Promise<void> => {
        this._localCulture = this._profileService.user.getProperty("CultureCode") || "en-us";
        this._loyaltyData = this._profileService.user.getProperty("LoyaltyData");
        this._offersData = await this._getOffersData();
        this._nextRewardInfo = await this._profileService.getNextRewardsInfo();
        this._rewardsHistory = await this._getLoyaltyHistory();
    }

    /**
     * Gets the combined offers data for the rewards page.
     * @returns {Promise<IUserRewardOffersDetails[]>} - The combined offers data.}
     */
    private readonly _getOffersData = async (): Promise<IUserRewardOffersDetails[]> => {
        const rewards = this._loyaltyData?.Rewards || [];
        const offers = this._profileService.user.getProperty("Offers") || null;
        const walletCoupons = this._profileService.user.getProperty("SingleUseCoupons") || [];
        const combinedOffers = await this._profileService.combineUserOffers(offers, rewards, false, walletCoupons);
        const paginatedOffers = this._paginateList(combinedOffers, 0);
        const offerDetails = paginatedOffers.map(x => ({
            pagination: x.pagination,
            isDisplayed: false,
            combinedOffers: x.list
        })) as IUserRewardOffersDetails[];
        return offerDetails;
    }

    /**
     * Gets the loyalty history data for the rewards page.
     * @returns {Promise<IMyHistoryDetails[]>} - The loyalty history data for the user. }
     */
    private readonly _getLoyaltyHistory = async (): Promise<IMyHistoryDetails[]> => {
        const dateNow = new Date().toLocaleDateString();
        const loyaltyHistory = await this._profileService.fetchLoyaltyHistory(dateNow) || [];

        // Determine expired entries and append additional records with "EXPIRED" suffix
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const expiredAdditions: IMyHistory[] = loyaltyHistory
            .filter(item => item.expirationDate && new Date(item.expirationDate) < today)
            .map(item => {
                return {
                    date: item.expirationDate!,
                    expirationDate: item.expirationDate,
                    info: item.info,
                    earned: false,
                    type: item.type
                };
            });

        // Merge original history with expired entries
        const combinedHistory = [...loyaltyHistory, ...expiredAdditions];

        // Keep only those within the rolling history window
        const filteredHistory = combinedHistory.filter(x => {
            const historyDate = new Date(this._historyDate);
            return new Date(x.date) > historyDate
        });

        // Sort by date descending to show newest first
        filteredHistory.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

        // Paginate and wrap into details
        const paginatedHistory = this._paginateList(filteredHistory, 0);
        const historyDetails = paginatedHistory.map(x => ({
            pagination: x.pagination,
            isDisplayed: false,
            loyaltyItems: x.list
        })) as IMyHistoryDetails[];

        return historyDetails;
    };

    /**
     * Paginates the list of items for the rewards page
     * @param initialList -  Represents either IUserRewardsOffersDTO or IMyHistory
     * @param currentPage - The current page number
     * @returns {Promise<{ pagination: number, list: T[] }[]>} - The paginated list of items.
     */
    private readonly _paginateList = <T>(initialList: T[], currentPage: number): { pagination: number, list: T[] }[] => {
        const paginatedList: { pagination: number, list: T[] }[] = [];
        const copiedList = Array.from(initialList);

        if (copiedList.length > 5) {
            const splicedOffers = copiedList.splice(0, this._maxPerPage);
            paginatedList.push({
                pagination: currentPage + 1,
                list: splicedOffers
            });
            const newPaginatedList = this._paginateList(copiedList, currentPage + 1);
            paginatedList.push(...newPaginatedList);
        } else {
            paginatedList.push({
                pagination: currentPage + 1,
                list: copiedList
            });
        }

        return paginatedList;
    }

    /**
     * Renders the rewards page sections asynchronously.
     */
    private async _renderPageAsync(): Promise<void> {
        const divPointsSummary = document.getElementById("div_points_summary_main");
        if (divPointsSummary) {
            await this._buildPointsSummary(divPointsSummary);
        }

        const divUserInfo = document.getElementById("div_user_info_main");
        if (divUserInfo) {
            await this._buildUserInfoSection(divUserInfo);
        }

        const divRedeemPoints = document.getElementById("div_redeem_points_main");
        if (divRedeemPoints) {
            this._buildRedeemPointsSection(divRedeemPoints);
        }

        const divOffers = document.getElementById("div_offers_main");
        if (divOffers) {
            await this._buildOffersSection(divOffers);
        }

        const divRewardsActivity = document.getElementById("div_rewards_activity_main");
        if (divRewardsActivity) {
            await this._buildRewardsActivity(divRewardsActivity);
        }
    }

    /**
     * Builds the points summary section of the rewards page.
     * @param parentDiv - The parent div to append the points summary to.
     */
    private async _buildPointsSummary(parentDiv: HTMLElement): Promise<void> {
        if (!this._isLoyaltyPointsRedemptionEnabled()) {
            parentDiv.appendChild(this._buildUserGreeting());
            return;
        }

        const divTotalPoints = document.createElement("div");
        divTotalPoints.id = "div_total_points";
        divTotalPoints.classList.add("points-summary-base", "total-points");
        divTotalPoints.innerText = this._loyaltyData?.CurrentPoints ? this._loyaltyData?.CurrentPoints.toString() : '';

        parentDiv.appendChild(divTotalPoints);

        const divAvailablePoints = document.createElement("span");
        divAvailablePoints.id = "spn_available_points";
        divAvailablePoints.classList.add("points-summary-base", "available-points");
        divAvailablePoints.innerText = this._localizationService.names("AVAILABLE_POINTS_TEMPLATE")
            .replaceAll("{{points_unit}}", this._getPointsUnitText());
        parentDiv.appendChild(divAvailablePoints);

        const divBalanceDate = document.createElement("span");
        divBalanceDate.id = "spn_balance_date";
        divBalanceDate.classList.add("points-summary-base", "balance-date");

        const lastUpdatedDate = this._loyaltyData?.PointsLastUpdatedDate ? this._loyaltyData.PointsLastUpdatedDate.split("T")[0] : '';
        if (lastUpdatedDate) {
            const formattedDate = Util.formatDateWithOptions(lastUpdatedDate, this._localCulture, this._dateFormatOptions);
            divBalanceDate.innerText = `${this._localizationService.names("BalanceAsOf")} ${formattedDate}`;
        } else {
            divBalanceDate.innerText = `${this._localizationService.names("BalanceAsOf")} Unknown Date`;
        }
        parentDiv.appendChild(divBalanceDate);
    }

    private _isLoyaltyPointsRedemptionEnabled(): boolean {
        return this._profileService.loyaltyProvider.hasPointRedemption();
    }

    private _buildUserGreeting(): HTMLDivElement {
        const greeting = this._localizationService
            .names("RewardsGreeting")
            .replace("{{first_name}}", this._profileService.user.getProperty("Name") || "")
            .toUpperCase();
        const divGreeting = document.createElement("div");
        divGreeting.classList.add("rewards-user-info-greeting");
        divGreeting.innerText = greeting;
        return divGreeting;
    }

    /**
     * Builds the user info section of the rewards page.
     * @param parentDiv
     */
    private async _buildUserInfoSection(parentDiv: HTMLElement): Promise<void> {
        let html = "";
        let userGreetingHtml = "";
        let pointsMsg = "";
        let progressBarHtml = "";
        let loyaltyInfoHtml = "";

        if (this._isLoyaltyPointsRedemptionEnabled()) {
            const userName = (`${this._localizationService.names("Hey")} ${this._profileService.user.getProperty("Name")}`).toUpperCase();
            userGreetingHtml = `<div class="rewards-user-info-greeting">${userName},</div>`;
        }

        if (this._nextRewardInfo) {
            const earnedPoints = this._nextRewardInfo?.reward ? this._nextRewardInfo.points : 0;
            const points = this._nextRewardInfo.points;
            const pointsUnit = this._getPointsUnitText();

            const rewardNameTxt = this._nextRewardInfo.reward?.Names
                ? this._localizationService.GetName(this._nextRewardInfo.reward.Names)
                : "Unknown Reward";

            const nextRewardMsg = this._localizationService.names("YOUR_POINTS_AWAY_FROM_REWARD")
                .replaceAll("{{points}}", points.toString())
                .replaceAll("{{points_unit}}", pointsUnit)
                .replaceAll("{{reward_name}}", rewardNameTxt);

            const keepUnlockingTxt = this._localizationService.names("RewardsKeepUnlocking");
            const bangIndex = keepUnlockingTxt.indexOf('!') + 1;
            const noNextRewardMsg = [keepUnlockingTxt.slice(0, bangIndex), '<br>', keepUnlockingTxt.slice(bangIndex)].join('');
            pointsMsg = earnedPoints > 0 ? `<span>${nextRewardMsg}</span>` : noNextRewardMsg;

            if (earnedPoints > 0) {
                progressBarHtml = this._buildProgressBar();
            }
            loyaltyInfoHtml = this._buildLoyaltyInfo();
        }

        html += userGreetingHtml;
        html += `<div class="rewards-user-info-msg">${pointsMsg}</div>`;

        html += progressBarHtml;
        html += loyaltyInfoHtml;
        Util.setElement("innerHTML", parentDiv, html);
    }

    /**
     * Builds the progress bar for the rewards page.
     * @returns {string} - The HTML string for the progress bar.
     */
    private _buildProgressBar(): string {
        let html = "";

        if (this._nextRewardInfo) {
            const nextRewardPoints = this._nextRewardInfo.reward?.Points || 0;
            const currentBarPoints = nextRewardPoints - this._nextRewardInfo.points;

            const width = Math.round((currentBarPoints / nextRewardPoints) * 100);
            const widthDiv = (width > 100 ? 100 : width) + "%";

            const divBar = document.createElement("div");
            divBar.classList.add("loyalty-points-bar");
            const divUnfilledBarContainer = document.createElement("div");
            divUnfilledBarContainer.classList.add("loyalty-points-bar", "unfilled-container");
            const divUnfilledBar = document.createElement("div");
            divUnfilledBar.classList.add("loyalty-points-bar", "unfilled");
            const divUnfilledTarget = document.createElement("div");
            divUnfilledTarget.classList.add("loyalty-points-bar", "unfilled", "complete");
            const divUnfilledTargetTxt = document.createElement("div");
            divUnfilledTargetTxt.classList.add("loyalty-points-bar", "complete-text");
            divUnfilledTargetTxt.innerText = nextRewardPoints.toString();
            // let leftAdjustment = nextRewardPoints.toString().length > 1 ?
            //     (nextRewardPoints.toString().length > 2 ? '10' : '30') : '40';
            // divUnfilledTargetTxt.style.left = leftAdjustment + '%';

            divUnfilledTarget.appendChild(divUnfilledTargetTxt);
            divUnfilledBarContainer.appendChild(divUnfilledBar);
            divUnfilledBarContainer.appendChild(divUnfilledTarget);
            divBar.appendChild(divUnfilledBarContainer);

            const divFilledBarContainer = document.createElement("div");
            divFilledBarContainer.classList.add("loyalty-points-bar", "filled-container");
            const divFilledBar = document.createElement("div");
            divFilledBar.classList.add("loyalty-points-bar", "filled");
            divFilledBar.style.width = widthDiv;
            const divFilledTarget = document.createElement("div");
            divFilledTarget.classList.add("loyalty-points-bar", "filled", "status");
            const divFilledTargetTxt = document.createElement("div");
            divFilledTargetTxt.classList.add("loyalty-points-bar", "loyalty-status-text");
            divFilledTargetTxt.innerText = currentBarPoints.toString();
            // leftAdjustment = nextRewardPoints.toString().length > 1 ?
            //     (nextRewardPoints.toString().length > 2 ? '20' : '30') : '40';
            // divFilledTargetTxt.style.left = leftAdjustment + '%';

            divFilledTarget.appendChild(divFilledTargetTxt);
            divFilledBarContainer.appendChild(divFilledBar);
            divFilledBarContainer.appendChild(divFilledTarget);
            divBar.appendChild(divFilledBarContainer);

            html = divBar.outerHTML;
        }

        return html;
    }

    /**
     * Builds the loyalty info section for the rewards page.
     * @returns {string} - The HTML string for the loyalty info section.
     */
    private _buildLoyaltyInfo(): string {
        let html = "";

        const joinedDate = this._loyaltyData?.LoyaltyJoinedDate ? Util.formatDateWithOptions(this._loyaltyData.LoyaltyJoinedDate.split("T")[0], this._localCulture, this._dateFormatOptions) : "N/A";
        const lifetimePoints = this._loyaltyData?.EarnedPoints ? this._loyaltyData?.EarnedPoints.toString() : '';
        const lastOrderDate = this._loyaltyData?.PointsLastUpdatedDate ? Util.formatDateWithOptions(this._loyaltyData.PointsLastUpdatedDate.split("T")[0], this._localCulture, this._dateFormatOptions) : "N/A";
        const lifetimePointsText = this._localizationService.names("LIFETIME_POINTS_TEMPLATE")
            .replaceAll("{{points_unit}}", this._getPointsUnitText());

        html += `
            <div class="loyalty-info">
                <span ltag="LoyaltyMemberSince">${this._localizationService.names("LoyaltyMemberSince")}</span> ${joinedDate}<br>
                <span>${lifetimePointsText}</span>: <b>${lifetimePoints}</b><br>
                <span ltag="LastOrderDate">${this._localizationService.names("LastOrderDate")}</span>: ${lastOrderDate}<br>
            </div>
        `;

        return html;
    }

    private _buildRedeemPointsSection(parentDiv: HTMLElement): void {
        if (this._isLoyaltyPointsRedemptionEnabled()) {
            this._renderRedeemPointsHeader(parentDiv);
            this._renderRedeemPointsContent(parentDiv);
        }
    }

    private _renderRedeemPointsHeader(parentDiv: HTMLElement): void {
        const h2RedeemPointsHeader = document.createElement("h2");
        h2RedeemPointsHeader.classList.add("rewards-section-header");
        h2RedeemPointsHeader.innerText = this._localizationService.names("REDEEM_POINTS_TEMPLATE")
            .replaceAll("{{points_unit}}", this._getPointsUnitText());
        parentDiv.appendChild(h2RedeemPointsHeader);
    }

    private _renderRedeemPointsContent(parentDiv: HTMLElement): void {
        const redeemGroups = this._data
            .getBrandProperty("RedeemItemDisplayGroups")
            .filter((group) => group.MinPoints > 0);

        if (!redeemGroups || redeemGroups.length === 0) {
            this._appendNoItemsCard(parentDiv, this._localizationService.names("NoRedeemItemGroups"));
        } else {
            const redeemGroupsElement = this._getRedeemItemGroupsElement(redeemGroups);
            parentDiv.appendChild(redeemGroupsElement);
        }
    }

    private _getRedeemItemGroupsElement(redeemGroups: IDataRedeemItemDisplayGroup[]): HTMLElement {
        redeemGroups.sort((a, b) => a.SortingIndex - b.SortingIndex);

        const containerDiv = document.createElement("div");
        containerDiv.classList.add("redeem-item-groups-container");

        redeemGroups.forEach(redeemGroup => {
            const groupCard = this._buildRedeemItemsGroupCard(redeemGroup);
            containerDiv.appendChild(groupCard);
        });

        return containerDiv;
    }

    private _buildRedeemItemsGroupCard(redeemGroup: IDataRedeemItemDisplayGroup): HTMLElement {
        const itemsGroupCard = document.createElement("div");
        itemsGroupCard.classList.add("redeem-item-group-card");
        itemsGroupCard.setAttribute("tabindex", "0");

        itemsGroupCard.addEventListener("click", async () => {
            await this._onRedeemGroupClick(redeemGroup);
        });
        Util.addKeyboardActivation(itemsGroupCard, () => this._onRedeemGroupClick(redeemGroup));

        const groupName = this._localizationService.GetName(redeemGroup.Names);

        const pointsText = this._getPointsUnitText();
        let groupPointsText = '';

        if (redeemGroup.MinPoints == redeemGroup.MaxPoints) {
            groupPointsText = `${redeemGroup.MinPoints} ${pointsText}`;
        } else {
            groupPointsText = `${redeemGroup.MinPoints} - ${redeemGroup.MaxPoints} ${pointsText}`;
        }

        const html = `
            <img class="redeem-item-group-card-image" src="${redeemGroup.ImageUrl}" alt="" />
            <div class="redeem-item-group-card-name" >
                <h3 class="redeem-item-group-card-name-text">${groupName}</h3>
            </div>
            <div class="redeem-item-group-card-points" >
                <span class="redeem-item-group-card-points-text">${groupPointsText}</span>
            </div>
        `;

        itemsGroupCard.innerHTML = html;
        return itemsGroupCard;
    }

    private async _onRedeemGroupClick(redeemGroup: IDataRedeemItemDisplayGroup): Promise<void> { 
        if (this._data.storeKey) {
            this._eventBus.emit(EventTypes.MODAL_OPEN, Modals.REWARD_GROUP_ITEMS, {
                redeemItemDisplayGroup: redeemGroup,
                loyaltyPoints: this._loyaltyData?.CurrentPoints ?? 0,
            } as IRewardGroupItemsProps);
        } else {
            const options :ILocationProps = {
                onContinue: this._handleLocationsContinue,
            }
            this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.LOCATIONS, options)
        }
    }

    private readonly _handleLocationsContinue = (): void => {
        if (Router.path !== Pages.REWARDS) {
            this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.REWARDS);
        }
    }

    /**
     * Builds the offers section of the rewards page.
     * @param parentDiv - The parent div to append the offers section to.
     */
    private async _buildOffersSection(parentDiv: HTMLElement): Promise<void> {
        const h2OffersHeader = document.createElement("h2");
        h2OffersHeader.classList.add("rewards-section-header");
        h2OffersHeader.setAttribute("ltag", "Offers")
        h2OffersHeader.innerText = this._localizationService.names("Offers");
        parentDiv.appendChild(h2OffersHeader);

        // _paginateList always yields at least one page, so check for actual offers to reach the "No offers" card.
        if (this._offersData?.some(page => page.combinedOffers?.length)) {
            this._renderPaginatedData(
                parentDiv,
                this._paginationState.offersPagesOpened,
                this._offersData,
                true,
            );
        } else {
            this._appendNoItemsCard(parentDiv, this._localizationService.names("NoOffers"));
        }
    }

    /**
     * Builds the rewards activity section of the rewards page.
     * @param parentDiv - The parent div to append the rewards activity section to.
     */
    private async _buildRewardsActivity(parentDiv: HTMLElement): Promise<void> {
        const h2RewardsActivityHeader = document.createElement("h2");
        h2RewardsActivityHeader.classList.add("rewards-section-header");
        h2RewardsActivityHeader.innerText = this._localizationService.names("RewardsActivity");
        h2RewardsActivityHeader.setAttribute("ltag", "RewardsActivity");
        parentDiv.appendChild(h2RewardsActivityHeader);

        let noRewardsTxt = this._localizationService.names("NoRewardsActivity_Single");
        if (this._maxRewardsHistoryDays > 1) {
            noRewardsTxt = this._localizationService.names("NoRewardsActivity_Plural").replace("{{number}}", this._maxRewardsHistoryDays.toString());
        }

        if (this._rewardsHistory && this._rewardsHistory.length > 0) {
            this._renderPaginatedData(
                parentDiv,
                this._paginationState.historyPagesOpened,
                this._rewardsHistory,
                false
            );
        } else {
            this._appendNoItemsCard(parentDiv, noRewardsTxt);
        }
    }

    /**
     * Handles rendering paginated data for either the offers or rewards activity sections of the rewards page and 
     * the Show More button to load more items.
     * @param parentDiv - The parent div to append the paginated data to.
     * @param paginationState - The current pagination state.
     * @param data - The data to render, either IUserRewardOffersDetails[] or IMyHistoryDetails[].
     * @param isOffer - True if the data is for offers, false if it is for rewards activity.
     */
    private _renderPaginatedData<T>(
        parentDiv: HTMLElement,
        paginationState: number,
        data: { isDisplayed: boolean, combinedOffers?: T[], loyaltyItems?: T[] }[] | null,
        isOffer: boolean
    ): void {
        if (data && paginationState > 0) {
            const pagesArray = Array.from({ length: paginationState }, (_, i) => i);
            this._renderPageItems(parentDiv, pagesArray, data, isOffer);

            if (data.length > 1) {
                this._addShowMoreButton(parentDiv, data, isOffer, this._renderMoreItems);
            }
        }
    }

    /**
     * Handles rendering paginated data for either the offers or rewards activity sections of the rewards page
     * @param parentDiv - The parent div to append the paginated data to.
     * @param pagesArray - The array of page numbers to render.
     * @param data - The data to render, either IUserRewardOffersDetails[] or IMyHistoryDetails[].
     * @param isOffer - True if the data is for offers, false if it is for rewards activity.
     * @returns {boolean} - True if no items were rendered, false otherwise.
     */
    private _renderPageItems<T>(
        parentDiv: HTMLElement,
        pagesArray: number[],
        data: { isDisplayed: boolean, combinedOffers?: T[], loyaltyItems?: T[] }[],
        isOffer: boolean
    ): boolean {
        let noItems = true;
        for (const pageIdx of pagesArray) {
            if (!data[pageIdx] || (isOffer ? data[pageIdx].combinedOffers?.length === 0 : data[pageIdx].loyaltyItems?.length === 0)) {
                continue;
            }

            const pageData = data[pageIdx];
            const itemsToRender = isOffer ? pageData.combinedOffers : pageData.loyaltyItems;
            if (itemsToRender) {
                this._renderList(itemsToRender, parentDiv, isOffer);
                pageData.isDisplayed = true;
                noItems = false;
            }
        }
        return noItems;
    }

    /**
     * Appends a card to the parent div to display custom text to explain there are no rewards to display.
     * @param parentDiv - The parent div to append the card to.
     * @param message - The message to display on the card.
     */
    private readonly _appendNoItemsCard = (parentDiv: HTMLElement, message: string): void => {
        const spanText = document.createElement("span");
        spanText.classList.add("rewards-card-base-text", "rewards-no-items");
        spanText.innerText = message;

        const divMessage = document.createElement("div");
        divMessage.classList.add("rewards-card-base", "rewards-no-items");
        divMessage.appendChild(spanText);

        parentDiv.appendChild(divMessage);
    }

    /**
     * Renders the list of items for either the offers or rewards activity sections of the rewards page.
     * @param items - Represents either IUserRewardOffersDTO[] or IMyHistory[]
     * @param parent - The parent div to append the items to.
     * @param isOffer - True if the items are offers, false if the items are rewards activity.
     */
    private readonly _renderList = <T>(items: T[], parent: HTMLElement, isOffer: boolean): void => {
        items.forEach((item) => {
            const divCard = isOffer
                ? this._buildOfferCard(item as unknown as IUserRewardOffersDTO)
                : this._buildHistoryCard(item as unknown as IMyHistory);

            if (divCard) {
                this._insertBeforeShowMoreButton(parent, divCard);
            }
        });
    }

    /**
     * Builds a reward card for an offer.
     * @param offer - The combined offer to render.
     * @returns {HTMLElement | null} - The offer card element, or null if the offer has no display name.
     */
    private readonly _buildOfferCard = (offer: IUserRewardOffersDTO): HTMLElement | null => {
        if (offer.Names.length === 0) {
            return null;
        }

        const offerName = this._localizationService.GetName(offer.Names).toUpperCase();
        const offerExp = this._buildOfferExpiryHtml(offer);
        return this._createRewardCard(offer.OfferId, offerName, offerExp, offer.Used, offer.IsReward, true);
    }

    /**
     * Builds the expiry/single-use-coupon details HTML for an offer card.
     * @param offer - The offer to build the details HTML for.
     * @returns {string} - The HTML string for the offer's expiry and single-use-coupon details.
     */
    private readonly _buildOfferExpiryHtml = (offer: IUserRewardOffersDTO): string => {
        let offerExp = offer.ExpiryDate ? `<div><span ltag="RedeemBy">${this._localizationService.names("RedeemBy")}</span> ${Util.formatDate(new Date(offer.ExpiryDate))}</div>` : "";
        if (offer.SingleUseCouponCode) {
            offerExp += `<div><span ltag="SingleUseCoupon">${this._localizationService.names("SingleUseCoupon")}</span>: ${offer.SingleUseCouponCode}</div>`;
        }
        return offerExp;
    }

    /**
     * Builds a reward card for a rewards-activity entry.
     * @param reward - The rewards-activity entry to render.
     * @returns {HTMLElement | null} - The history card element, or null if the reward has no display name.
     */
    private readonly _buildHistoryCard = (reward: IMyHistory): HTMLElement | null => {
        const rewardName = this._buildRewardName(reward);
        if (!rewardName) {
            return null;
        }

        const earnedDate = reward.date ? Util.formatDate(new Date(reward.date)) : "N/A";
        return this._createRewardCard('', rewardName, earnedDate, false, true, false);
    }

    /**
     * Builds the reward name for the rewards activity section of the rewards page.
     * @param reward - The reward to build the name for
     * @returns {string} - The HTML string for the reward name.
     */
    private readonly _buildRewardName = (reward: IMyHistory): string => {
        // Attempt to parse JSON info; fallback to raw string
        let parsed: any;
        try {
            parsed = JSON.parse(reward.info);
        } catch {
            parsed = null;
        }

        // Determine display name
        let original: string;
        let namesArr: IName[] = [];
        if (Array.isArray(parsed) && parsed.every(item => item?.NAM)) {
            namesArr = parsed;
            original = this._localizationService.GetName(namesArr).toUpperCase();
        } else {
            // info is not JSON array of names; use raw info
            original = reward.info.toUpperCase();
        }

        // Config per type
        const config = {
            [LoyaltyProgramType.REWARDS]: {
                earnedTag: reward.earned ? 'EarnedREW' : 'Redeemed',
                infoText: original
            },
            [LoyaltyProgramType.REDEEM_POINTS]: {
                earnedTag: reward.earned ? 'EarnedPTS' : 'Redeemed',
                infoText: reward.info,
                typeTag: this._getPointsUnitText().toLowerCase()
            },
            [LoyaltyProgramType.MEMBERSHIP]: {
                earnedTag: reward.earned ? 'Achieved' : 'DowngradedTo',
                infoText: original
            }
        }[reward.type] || { infoText: original };

        // Expired case
        if (reward.expirationDate && reward.date === reward.expirationDate && !reward.earned) {
            config.earnedTag = "Expired";
        }

        // Build spans
        const earnedSpan = config.earnedTag ? `<span ltag="${config.earnedTag}">${this._localizationService.names(config.earnedTag)}</span>` : '';
        const hasInfoAttr = config.earnedTag !== undefined && reward.type !== LoyaltyProgramType.REDEEM_POINTS && namesArr.length > 0;
        const infoAttr = hasInfoAttr ? ` ltagj="${encodeURIComponent(reward.info)}"` : '';
        const infoSpan = `<span${infoAttr}>${config.infoText.toUpperCase()}</span>`;
        const typeSpan = config.typeTag ? `<span ltag="${config.typeTag}">${config.typeTag}</span>` : '';

        return [earnedSpan, infoSpan, typeSpan].join(' ').trim();
    };

    /**
     * Renders more items for either the offers or rewards activity sections of the rewards page.
     * @param parent - The parent div to append the items to.
     * @param button - The button to click to render more items.
     * @param data - Holds the data to render more items, either IUserRewardOffersDTO[] or IMyHistory[].
     * @param isOffer - True if the items are offers, false if the items are rewards activity.
     * @returns
     */
    private readonly _renderMoreItems = async <T>(
        parent: HTMLElement,
        button: HTMLElement,
        data: { isDisplayed: boolean, combinedOffers?: T[], loyaltyItems?: T[] }[],
        isOffer: boolean
    ): Promise<void> => {
        const firstNewItemIndex = parent.children.length - 2;

        for (const paginatedItem of data) {
            if (paginatedItem.isDisplayed) {
                continue;
            }

            const items = isOffer ? paginatedItem.combinedOffers : paginatedItem.loyaltyItems;
            if (items) {
                this._renderList(items, parent, isOffer);
                this._setEventListeners();
                paginatedItem.isDisplayed = true;
                this._updatePagination(isOffer);
                break;
            }
        }

        if (firstNewItemIndex < 0) {
            return;
        }

        const firstNewItem = parent.children.item(firstNewItemIndex) as HTMLElement;
        if (firstNewItem) {
            const card = firstNewItem.querySelector<HTMLElement>(".rewards-card-base");
            if (card) {
                card.scrollIntoView({ behavior: "smooth", block: "start", inline: "center" });
            }
        }

        if (data.every(x => x.isDisplayed)) {
            button.parentElement?.remove();
        }
    }

    private _updatePagination(isOffer: boolean): void {
        if (isOffer) {
            this._paginationState.offersPagesOpened += 1;
        } else {
            this._paginationState.historyPagesOpened += 1;
        }
    }

    /**
     * Creates a reward card for a given reward activity or offer.
     * @param id - The ID of the reward or offer.
     * @param name - The name of the reward or offer.
     * @param detailsHtml - If a reward, then it is when the reward was earned. If offer, then it is the expiry/single-use-coupon details HTML.
     * @param isUsed - True if the offer has been used, false otherwise (does not apply to a reward activity).
     * @param isReward - True if the item is a reward, false if it is an offer.
     * @param withRedeemBtn - True if the card should have a redeem button, false otherwise.
     * @returns {HTMLElement} - The reward card element.
     */
    private _createRewardCard(id: string, name: string, detailsHtml: string, isUsed: boolean, isReward: boolean, withRedeemBtn: boolean): HTMLElement {
        const divRewardCard = document.createElement("div");
        divRewardCard.classList.add("rewards-card-base");
        if (withRedeemBtn) {
            divRewardCard.classList.add("with-btn");
        }

        const divRewardTxtContainer = document.createElement("div");
        divRewardTxtContainer.classList.add("psuedo", "rewards-card-base-text");

        const rewardName = document.createElement("h3");
        rewardName.id = `h3_rewards_card_name_${id}`;
        rewardName.classList.add("rewards-card-name");
        rewardName.innerHTML = name;
        divRewardTxtContainer.appendChild(rewardName);

        const rewardDate = document.createElement("span");
        rewardDate.classList.add("rewards-card-date");
        rewardDate.innerHTML = detailsHtml;
        divRewardTxtContainer.appendChild(rewardDate);
        divRewardCard.appendChild(divRewardTxtContainer);

        if (withRedeemBtn) {
            const btnRedeem = document.createElement("button");
            btnRedeem.innerHTML = isUsed ? this._localizationService.names("Void") : this._localizationService.names("Redeem");
            btnRedeem.id = `btn_rewards_offers_redeem_${id}`;
            btnRedeem.name = "btn_rewards_offers_redeem";
            btnRedeem.setAttribute(isReward ? "data-rid" : "data-oid", id);
            btnRedeem.classList.add("rewards-redeem-button", "button-link");
            divRewardCard.appendChild(btnRedeem);
        }

        return divRewardCard;
    }

    /**
     * Inserts a new element before the show more button if it exists, otherwise appends the new element to the parent.
     * @param parent - The parent element to append the new element to.
     * @param newElement - The new element to append to the parent.
     */
    private readonly _insertBeforeShowMoreButton = (parent: HTMLElement, newElement: HTMLElement): void => {
        const existingButton = parent.querySelector(".rewards-show-more-btn") as HTMLElement;
        if (existingButton) {
            const wrapperCard = existingButton.closest(".rewards-card-base") as HTMLElement;
            if (wrapperCard && wrapperCard.parentNode === parent) {
                parent.insertBefore(newElement, wrapperCard);
            } else {
                parent.appendChild(newElement);
            }
        } else {
            parent.appendChild(newElement);
        }
    }

    /**
     * Adds a show more button to the parent element to display more items.
     * @param parent - The parent element to append the show more button to.
     * @param data - Holds the data to render more items, either IUserRewardOffersDTO[] or IMyHistory[].
     * @param isOffer - True if the items are offers, false if the items are rewards activity.
     * @param showMoreCallback - The callback function to render more items.
     */
    private readonly _addShowMoreButton = <T>(
        parent: HTMLElement,
        data: { isDisplayed: boolean, combinedOffers?: T[], loyaltyItems?: T[] }[],
        isOffer: boolean,
        showMoreCallback: (parent: HTMLElement, button: HTMLElement, data: { isDisplayed: boolean, combinedOffers?: T[], loyaltyItems?: T[] }[], isOffer: boolean) => Promise<void>
    ): void => {
        const button = document.createElement("button");
        button.classList.add("rewards-show-more-btn", "secondary");
        button.innerText = this._localizationService.names("ShowMore");

        button.addEventListener("click", async () => {
            await showMoreCallback(parent, button, data, isOffer);
        });

        const cardWrapper = document.createElement("div");
        cardWrapper.classList.add("rewards-card-base", "psuedo", "flex-center-all");

        cardWrapper.appendChild(button);
        parent.appendChild(cardWrapper);
    }

    /**
     * Sets the event listeners for the rewards page.
     */
    private readonly _setEventListeners = (): void => {
        const redeemBtns = Array.from(document.getElementsByName("btn_rewards_offers_redeem"));
        for (const button of redeemBtns) {
            if (button.onclick == null) {
                const rId = button.dataset.rid;
                const oId = button.dataset.oid;
                button.onclick = () => {
                    this._redeemOnClick(rId, oId);
                }
            }
        }
    }

    /**
     * Handles the redeem button click event for an offer.
     * @param rewardId - The ID of the reward to redeem if applicable.
     * @param offerCode - The code of the offer to redeem if applicable.
     * @returns
     */
    private readonly _redeemOnClick = async (rewardId?: string, offerCode?: string): Promise<void> => {
        const spinner = new LoaderSpinner(this._element);
        const result = await this._profileService.redeemOfferAsync(rewardId, offerCode);
        if (result.offerId) {
            this._updateOfferCard(result.offerId, result.isUsed);
        }
        spinner.destroy();

        if (this._dataLayer) {
            let rewardName = '';
            if (rewardId)
                rewardName = this._localizationService.GetName(this._loyaltyData?.Rewards.find(x => x.RewardId === Number(rewardId))?.Names ?? []);
            if (offerCode) {
                const couponId = this._profileService.user.getProperty("Offers")?.Codes?.find(x => x.CouponCode.toLowerCase() === offerCode.toLowerCase())?.CouponId;
                if (couponId)
                    rewardName = this._localizationService.GetName(this._profileService.user.getProperty("Offers")?.Coupons.find(x => x.CouponId === couponId)?.Names.map(x => ({ CULT: x.lc, NAM: x.text })) ?? []);
                else
                    rewardName = this._localizationService.GetName(this._profileService.user.getSingleUseCouponInfo(offerCode)?.names.map(x => ({ CULT: x.culture, NAM: x.name })) ?? []);
            }

            this._dataLayer.rewards_redeem(rewardName, offerCode ? ContentType.OFFER : ContentType.POINTS, this._data.getProperty("StoreInfo").storeCode.toString());
        }
    }

    /**
     * Updates the offer card's Redeem/Void button text.
     * @param offerId - The ID of the reward or offer
     * @param isUsed - The status of the coupon if it is currently used or not used
     */
    private readonly _updateOfferCard = (offerId: string, isUsed: boolean): void => {
        if (offerId) {
            Util.setElement("innerHTML", `btn_rewards_offers_redeem_${offerId}`, isUsed ? this._localizationService.names("Void") : this._localizationService.names("Redeem"));
        }
    }

    /**
     * Returns the localized loyalty unit label for the current loyalty configuration.
     * @returns {string} The localized loyalty unit name.
     */
    private _getPointsUnitText(): string {
        const loyaltyInfo = this._data.getLoyaltyInfo();
        return this._localizationService.getLoyaltyUnitName(loyaltyInfo);
    }
}